import React, { useState } from 'react';
import { X, Plus, Minus, Clock, Shuffle } from 'lucide-react';
import { RoomState, User } from '../types';
import EmojiPicker from './EmojiPicker';

interface CreateRoomModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (roomData: {
    name: string;
    states: RoomState[];
    currentState: RoomState;
    size: 'small' | 'medium' | 'large';
    viewMode: 'minimal' | 'detailed';
    autoResetState?: {
      stateId: string;
      afterMinutes: number;
    };
  }) => void;
}

const CreateRoomModal: React.FC<CreateRoomModalProps> = ({
  isOpen,
  onClose,
  onSubmit
}) => {
  const [name, setName] = useState('');
  const [states, setStates] = useState<RoomState[]>([
    { id: crypto.randomUUID(), name: 'Available', color: '#22c55e', emoji: '✅' },
    { id: crypto.randomUUID(), name: 'Occupied', color: '#ef4444', emoji: '🔴' }
  ]);
  const [size, setSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [viewMode, setViewMode] = useState<'minimal' | 'detailed'>('detailed');
  const [enableAutoReset, setEnableAutoReset] = useState(false);
  const [autoResetMinutes, setAutoResetMinutes] = useState(60);
  const [autoResetState, setAutoResetState] = useState<string>(states[0].id);
  const [showEmojiPicker, setShowEmojiPicker] = useState<string | null>(null);

  const generateRandomColor = () => {
    const colors = [
      '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e',
      '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
      '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#64748b',
      '#6b7280', '#374151'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const addState = () => {
    const newState = { 
      id: crypto.randomUUID(),
      name: '',
      color: generateRandomColor(),
      emoji: '⚪'
    };
    setStates([...states, newState]);
  };

  const removeState = (id: string) => {
    if (states.length > 2) {
      setStates(states.filter(state => state.id !== id));
      if (autoResetState === id) {
        setAutoResetState(states[0].id);
      }
    }
  };

  const updateState = (id: string, updates: Partial<RoomState>) => {
    setStates(states.map(state =>
      state.id === id ? { ...state, ...updates } : state
    ));
  };

  const handleEmojiSelect = (stateId: string, emoji: string) => {
    updateState(stateId, { emoji });
    setShowEmojiPicker(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      states,
      currentState: states[0],
      size,
      viewMode,
      ...(enableAutoReset && {
        autoResetState: {
          stateId: autoResetState,
          afterMinutes: autoResetMinutes
        }
      })
    });
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-700">
            <div className="flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-200">Create New Room</h2>
              <button
                type="button"
                onClick={onClose}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Room Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                required
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">States</label>
                <button
                  type="button"
                  onClick={addState}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add State
                </button>
              </div>
              <div className="space-y-3">
                {states.map((state) => (
                  <div key={state.id} className="flex gap-3 items-center">
                    <input
                      type="text"
                      value={state.name}
                      onChange={(e) => updateState(state.id, { name: e.target.value })}
                      placeholder="State name"
                      className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                      required
                    />
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowEmojiPicker(showEmojiPicker === state.id ? null : state.id)}
                          className="w-12 h-10 px-2 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-center text-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                          title="Select emoji"
                        >
                          {state.emoji || '⚪'}
                        </button>
                        {showEmojiPicker === state.id && (
                          <div className="absolute top-full left-0 mt-1 z-50">
                            <EmojiPicker
                              onEmojiSelect={(emoji) => handleEmojiSelect(state.id, emoji)}
                              onClose={() => setShowEmojiPicker(null)}
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <input
                          type="color"
                          value={state.color}
                          onChange={(e) => updateState(state.id, { color: e.target.value })}
                          className="w-12 h-10 rounded-lg cursor-pointer border border-slate-300 dark:border-slate-600"
                        />
                        <button
                          type="button"
                          onClick={() => updateState(state.id, { color: generateRandomColor() })}
                          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                          title="Random color"
                        >
                          <Shuffle className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                        </button>
                      </div>
                    </div>
                    {states.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeState(state.id)}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                      >
                        <Minus className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Card Size
                </label>
                <select
                  value={size}
                  onChange={(e) => setSize(e.target.value as typeof size)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  View Mode
                </label>
                <select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value as typeof viewMode)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                >
                  <option value="minimal">Minimal</option>
                  <option value="detailed">Detailed</option>
                </select>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="enableAutoReset"
                  checked={enableAutoReset}
                  onChange={(e) => setEnableAutoReset(e.target.checked)}
                  className="rounded border-slate-300 dark:border-slate-600 text-blue-500 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
                <label htmlFor="enableAutoReset" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Enable Auto Reset
                </label>
              </div>

              {enableAutoReset && (
                <div className="pl-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Reset to State
                    </label>
                    <select
                      value={autoResetState}
                      onChange={(e) => setAutoResetState(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                    >
                      {states.map(state => (
                        <option key={state.id} value={state.id}>
                          {state.emoji} {state.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      After (minutes)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        value={autoResetMinutes}
                        onChange={(e) => setAutoResetMinutes(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                      />
                      <Clock className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-4 sm:p-6 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 font-medium"
            >
              Create Room
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRoomModal;